import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PrescriptionLensSupplyMode,
  type ProductSummary,
  type ProductVariantSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { Modal } from '@/components/ui/Modal';
import { useCurrency } from '@/hooks/useCurrency';
import { useProductQuery, useProductsQuery } from '@/modules/catalog/api/catalog.api';

export interface PickedVariant {
  variantId: string;
  sku: string;
  productName: string;
  variantName: string | null;
  isBatchTracked: boolean;
  /** The catalogue's own cost, offered as a starting point for the agreed one. */
  costPriceMinor: number | null;
}

interface VariantPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onPick: (variant: PickedVariant) => void;
  /** Already on the order: shown as such rather than offered a second time. */
  chosenVariantIds: readonly string[];
}

/** A lens made for the job it belongs to holds no stock, so it is never bought in. */
function isMadeToOrder(variant: ProductVariantSummary): boolean {
  return variant.prescriptionLens?.supplyMode === PrescriptionLensSupplyMode.MADE_TO_ORDER;
}

/**
 * Finding something to order.
 *
 * Searches the catalogue on the server and then opens one product's variants, which is how the rest
 * of the app picks a variant too. What cannot be bought is visibly refused rather than hidden: a
 * buyer looking for a lens they know exists should be told why it is not orderable, not left to
 * wonder whether the search is broken.
 */
export function VariantPicker({ isOpen, onClose, onPick, chosenVariantIds }: VariantPickerProps) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const [search, setSearch] = useState('');
  const [product, setProduct] = useState<ProductSummary | null>(null);

  const deferredSearch = useDeferredValue(search);
  const products = useProductsQuery({ pageSize: 10, search: deferredSearch, status: 'ACTIVE' });
  const detail = useProductQuery(product?.id);

  const close = () => {
    setProduct(null);
    setSearch('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      size="lg"
      title={t('purchasing.orders.picker.title')}
      description={t('purchasing.orders.picker.hint')}
    >
      <div className="space-y-4">
        <Input
          type="search"
          value={search}
          autoFocus
          onChange={(event) => {
            setSearch(event.target.value);
            setProduct(null);
          }}
          placeholder={t('purchasing.orders.picker.searchPlaceholder')}
          aria-label={t('purchasing.orders.picker.searchLabel')}
        />

        {!product ? (
          products.isPending && deferredSearch ? (
            <LoadingState />
          ) : (
            <ul className="divide-line divide-y" aria-label={t('purchasing.orders.picker.results')}>
              {(products.data?.items ?? []).map((row) => (
                <li key={row.id} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0">
                    <span className="text-ink block truncate text-sm font-medium" dir="auto">
                      {row.name}
                    </span>
                    <span className="text-muted text-xs">
                      {row.brandName ?? t('catalog.fields.noBrand')}
                    </span>
                  </span>
                  <Button variant="secondary" size="sm" onClick={() => setProduct(row)}>
                    {t('purchasing.orders.picker.choose')}
                  </Button>
                </li>
              ))}
              {products.data && products.data.items.length === 0 ? (
                <li className="text-muted py-3 text-sm">
                  {t('purchasing.orders.picker.noProducts')}
                </li>
              ) : null}
            </ul>
          )
        ) : detail.isPending ? (
          <LoadingState />
        ) : (
          <section aria-label={product.name} className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-ink text-sm font-semibold" dir="auto">
                {product.name}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setProduct(null)}>
                {t('common.back')}
              </Button>
            </div>

            <ul className="divide-line divide-y">
              {(detail.data?.variants ?? []).map((variant) => {
                const madeToOrder = isMadeToOrder(variant);
                const chosen = chosenVariantIds.includes(variant.id);
                const blocked = madeToOrder || !variant.isActive;

                return (
                  <li key={variant.id} className="flex flex-wrap items-center gap-3 py-2">
                    <span className="min-w-0 flex-1">
                      <span className="text-ink block text-sm">
                        {variant.variantName ?? product.name}
                      </span>
                      <span className="text-muted font-mono text-xs" dir="ltr">
                        {variant.sku}
                      </span>
                      {madeToOrder ? (
                        <span className="mt-0.5 block text-xs text-amber-700">
                          {t('purchasing.orders.picker.madeToOrder')}
                        </span>
                      ) : null}
                      {!variant.isActive ? (
                        <span className="mt-0.5 block text-xs text-amber-700">
                          {t('purchasing.orders.picker.inactive')}
                        </span>
                      ) : null}
                    </span>

                    <span className="text-muted text-sm" dir="ltr">
                      {variant.costPriceMinor === null
                        ? '—'
                        : t('purchasing.orders.picker.cost', {
                            cost: format(variant.costPriceMinor),
                          })}
                    </span>

                    {chosen ? (
                      <span className="text-muted text-xs">
                        {t('purchasing.orders.picker.alreadyAdded')}
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        disabled={blocked}
                        onClick={() =>
                          onPick({
                            variantId: variant.id,
                            sku: variant.sku,
                            productName: product.name,
                            variantName: variant.variantName,
                            isBatchTracked: variant.isBatchTracked,
                            costPriceMinor: variant.costPriceMinor,
                          })
                        }
                      >
                        {t('purchasing.orders.picker.add')}
                      </Button>
                    )}
                  </li>
                );
              })}
              {detail.data && detail.data.variants.length === 0 ? (
                <li className="text-muted py-3 text-sm">
                  {t('purchasing.orders.picker.noVariants')}
                </li>
              ) : null}
            </ul>
          </section>
        )}

        <Alert tone="info">{t('purchasing.orders.picker.taxNotice')}</Alert>
      </div>
    </Modal>
  );
}
